"use client";

import React, { useState, useEffect, useRef } from "react";

const BackToTopButton = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollPct, setScrollPct] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        const onScroll = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                const scrolled = window.scrollY;
                const total = document.documentElement.scrollHeight - window.innerHeight;
                setScrollPct(total > 0 ? Math.min((scrolled / total) * 100, 100) : 0);
                setIsVisible(scrolled > 300);
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    const scrollToTop = () => {
        setIsPressed(true);
        setTimeout(() => setIsPressed(false), 200);

        // Custom eased scroll — easeInOutQuad
        const start = window.scrollY;
        const duration = 1200;
        let startTime: number | null = null;

        const easeInOutQuad = (t: number) =>
            t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        const step = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            window.scrollTo(0, start * (1 - easeInOutQuad(progress)));
            if (progress < 1) requestAnimationFrame(step);
        };

        requestAnimationFrame(step);
    };

    // Progress ring calculations
    const size = 56;
    const strokeWidth = 3;
    const radius = (size - strokeWidth * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (scrollPct / 100) * circumference;

    return (
        <button
            onClick={scrollToTop}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-label="Back to top"
            className="fixed bottom-24 right-8 z-50 focus:outline-none group"
            style={{
                width: `${size}px`,
                height: `${size}px`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible
                    ? isPressed
                        ? "translateY(0) scale(0.95)"
                        : isHovered
                            ? "translateY(-2px) scale(1.05)"
                            : "translateY(0) scale(1)"
                    : "translateY(20px) scale(0.5)",
                pointerEvents: isVisible ? "auto" : "none",
                transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
        >
            {/* Glow ring (Theme-13 style) */}
            <div
                className="absolute inset-[-4px] rounded-full pointer-events-none"
                style={{
                    background: isHovered
                        ? "radial-gradient(circle, rgba(214,28,78,0.25) 0%, transparent 70%)"
                        : "transparent",
                    filter: "blur(6px)",
                    transform: "scale(1.4)",
                    transition: "background 0.5s ease",
                }}
            />

            {/* Progress ring SVG */}
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="absolute inset-0"
                style={{ transform: "rotate(-90deg)" }}
            >
                {/* Background track */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="rgba(214,28,78,0.12)"
                    strokeWidth={strokeWidth}
                />
                {/* Progress fill */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#D61C4E"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    style={{
                        transition: "stroke-dashoffset 0.2s ease",
                        filter: isHovered
                            ? "drop-shadow(0 0 6px rgba(214,28,78,0.5))"
                            : "none",
                    }}
                />
            </svg>

            {/* Button body (Theme-13 design) */}
            <div
                className="absolute inset-1 rounded-full flex items-center justify-center transition-all duration-400"
                style={{
                    background: isHovered
                        ? "#0f172a"
                        : "#D61C4E",
                    boxShadow: isHovered
                        ? "0 20px 40px rgba(0,0,0,0.4)"
                        : "0 12px 32px rgba(214,28,78,0.45)",
                    border: "1px solid rgba(255,255,255,0.1)",
                }}
            >
                {/* Inner shimmer ring */}
                <div className="absolute inset-[3px] rounded-full border border-white/10 pointer-events-none" />

                {/* Double-arrow scroll animation */}
                <div className="relative overflow-hidden h-5 w-5">
                    <div
                        className="flex flex-col"
                        style={{
                            transform: isHovered ? "translateY(-21px)" : "translateY(0)",
                            transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                    >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                        <svg className="w-5 h-5 text-white mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                    </div>
                </div>

                {/* Bottom reflection */}
                <div
                    className="absolute bottom-1 w-1/2 h-[2px] bg-white/20 blur-[1px] rounded-full transition-opacity"
                    style={{ opacity: isHovered ? 0 : 0.5 }}
                />
            </div>

            {/* Tooltip (Theme-13 style) */}
            <span
                className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg bg-[#0f172a] text-white text-[9px] font-black uppercase tracking-widest pointer-events-none whitespace-nowrap border border-white/10 shadow-xl"
                style={{
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered
                        ? "translateY(-50%) translateX(0)"
                        : "translateY(-50%) translateX(8px)",
                    transition: "all 0.3s ease",
                }}
            >
                Back to Top
            </span>

            {/* Ripple on click */}
            {isPressed && (
                <div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                        background: "radial-gradient(circle, rgba(214,28,78,0.35) 0%, transparent 70%)",
                        animation: "ripple 0.6s ease-out",
                    }}
                />
            )}

            <style jsx>{`
                @keyframes ripple {
                    0%   { transform: scale(0); opacity: 1; }
                    100% { transform: scale(2); opacity: 0; }
                }
            `}</style>
        </button>
    );
};

export default BackToTopButton;
